import React, { useEffect } from 'react';
import { SkeletonLines } from '@/components/Admin/UI/Skeleton';
import styled from 'styled-components';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { colors } from '@/constants/colors';
import { useUIStore } from '@/store/useUIStore';
import {
   listPublicResellersApi,
   type ApiPublicReseller,
} from '@/api/vendo';

/**
 * Buyer-facing list of resellers (①c-E). Same card design language as the
 * distributor listing (white card, stat pills, avatar) so the two tabs blend
 * — resellers use a blue accent, distributors gold. Flat ₱500/credit, no
 * minimum. Cards open the InquireReseller modal. Deep-link ?seller=<slug>
 * (from the shared landing page) auto-opens the matching reseller.
 */
const RESELLER_PRICE = 500;

const initialsOf = (name: string) =>
   name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((w) => w[0]?.toUpperCase() ?? '')
      .join('');

const ResellerMarket: React.FC = () => {
   const { handleShow } = useUIStore();
   const [params, setParams] = useSearchParams();

   const { data: resellers = [], isLoading } = useQuery({
      queryKey: ['vendo', 'public-resellers'],
      queryFn: listPublicResellersApi,
   });

   const openBuy = (r: ApiPublicReseller) => {
      handleShow({
         template: 'inquire-reseller',
         title: `Buy credits from ${r.displayName}`,
         windowType: 'desktop',
         data: r,
      });
   };

   const sellerSlug = params.get('seller');
   useEffect(() => {
      if (!sellerSlug || resellers.length === 0) return;
      const match = resellers.find((r) => r.slug === sellerSlug);
      if (match) {
         openBuy(match);
         const next = new URLSearchParams(params);
         next.delete('seller');
         setParams(next, { replace: true });
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [sellerSlug, resellers]);

   if (isLoading) return <SkeletonLines lines={3} />;
   if (resellers.length === 0) {
      return (
         <Empty>
            No resellers listed yet. Resellers appear here once a distributor
            approves them and they have credits to sell.
         </Empty>
      );
   }

   return (
      <>
         <Intro>
            Buy credits straight from a reseller at a flat{' '}
            <strong>₱{RESELLER_PRICE}/credit</strong> — no minimum, even a single
            credit. The reseller confirms your request and messages you where to pay.
         </Intro>
         <Grid>
            {resellers.map((r) => {
               const soldOut = r.availableCredits <= 0;
               return (
                  <Card key={r._id}>
                     <Head>
                        <AvatarWrap>
                           {r.photoUrl ? (
                              <Avatar
                                 src={r.photoUrl}
                                 alt={r.displayName}
                                 onError={(e) => {
                                    (e.target as HTMLImageElement).style.display = 'none';
                                 }}
                              />
                           ) : null}
                           <AvatarFallback>{initialsOf(r.displayName)}</AvatarFallback>
                        </AvatarWrap>
                        <HeadText>
                           <Name>{r.displayName}</Name>
                           <Sub>Reseller{r.upline ? ` · via ${r.upline}` : ''}</Sub>
                        </HeadText>
                     </Head>

                     {r.blurb && <Blurb>{r.blurb}</Blurb>}

                     <Stats>
                        <StatBox $highlight={!soldOut}>
                           <StatLabel>Available</StatLabel>
                           <StatVal $green={!soldOut}>
                              {soldOut ? '—' : r.availableCredits.toLocaleString()}
                           </StatVal>
                        </StatBox>
                        <StatBox>
                           <StatLabel>Per credit</StatLabel>
                           <StatVal>₱{RESELLER_PRICE}</StatVal>
                        </StatBox>
                        <StatBox>
                           <StatLabel>Minimum</StatLabel>
                           <StatVal>1</StatVal>
                        </StatBox>
                     </Stats>

                     <BuyBtn disabled={soldOut} onClick={() => openBuy(r)}>
                        {soldOut ? 'No credits right now' : 'Inquire / Buy →'}
                     </BuyBtn>
                  </Card>
               );
            })}
         </Grid>
      </>
   );
};

export default ResellerMarket;

/* ─── styles (mirrors the distributor card language) ─────────────────── */

const Intro = styled.p`
   font-size: 13px;
   line-height: 1.5;
   color: ${colors.GRAY3};
   margin: 0 0 16px;
   padding: 0 1rem;
   @media (max-width: 768px) { padding: 0 12px; }
`;

const Muted = styled.p`
   font-size: 13px;
   color: ${colors.GRAY5};
   padding: 16px 1rem;
`;

const Empty = styled.div`
   margin: 0 1rem;
   padding: 28px 16px;
   text-align: center;
   font-size: 13px;
   color: ${colors.GRAY5};
   background: var(--app-surface, ${colors.WHITE});
   border: 1px dashed ${colors.GRAY12};
   border-radius: 12px;
`;

const Grid = styled.div`
   display: grid;
   grid-template-columns: repeat(4, minmax(0, 1fr));
   gap: 14px;
   padding: 0 1rem;
   @media (max-width: 1200px) { grid-template-columns: repeat(3, minmax(0, 1fr)); }
   @media (max-width: 900px) { grid-template-columns: repeat(2, minmax(0, 1fr)); }
   @media (max-width: 560px) { grid-template-columns: minmax(0, 1fr); }
   @media (max-width: 768px) { padding: 0 12px; }
`;

const Card = styled.div`
   display: flex;
   flex-direction: column;
   gap: 10px;
   padding: 14px;
   border-radius: 14px;
   background: var(--app-surface, ${colors.WHITE});
   border: 1px solid ${colors.GRAY13};
   transition: transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease;
   &:hover {
      transform: translateY(-2px);
      border-color: ${colors.PRIMARY}55;
      box-shadow: 0 8px 24px rgba(55, 96, 249, 0.1);
   }
`;

const Head = styled.div`
   display: flex;
   align-items: center;
   gap: 10px;
   min-width: 0;
`;

const AvatarWrap = styled.div`
   position: relative;
   width: 44px;
   height: 44px;
   flex-shrink: 0;
   border-radius: 12px;
   overflow: hidden;
   box-shadow: 0 0 0 2px ${colors.PRIMARY}40;
`;

const Avatar = styled.img`
   position: absolute;
   inset: 0;
   width: 100%;
   height: 100%;
   object-fit: cover;
   z-index: 1;
`;

const AvatarFallback = styled.div`
   position: absolute;
   inset: 0;
   display: flex;
   align-items: center;
   justify-content: center;
   font-weight: 800;
   font-size: 16px;
   color: ${colors.WHITE};
   background: linear-gradient(135deg, ${colors.PRIMARY}, #6a4dff);
`;

const HeadText = styled.div`
   min-width: 0;
   display: flex;
   flex-direction: column;
   gap: 1px;
`;

const Name = styled.p`
   font-size: 14px;
   font-weight: 800;
   color: ${colors.GRAY_DARK};
   margin: 0;
   line-height: 1.2;
   white-space: nowrap;
   overflow: hidden;
   text-overflow: ellipsis;
`;

const Sub = styled.p`
   font-size: 10px;
   font-weight: 700;
   text-transform: uppercase;
   letter-spacing: 0.04em;
   color: ${colors.GRAY5};
   margin: 0;
   white-space: nowrap;
   overflow: hidden;
   text-overflow: ellipsis;
`;

const Blurb = styled.p`
   font-size: 12px;
   color: ${colors.GRAY3};
   margin: 0;
   line-height: 1.4;
   display: -webkit-box;
   -webkit-line-clamp: 2;
   -webkit-box-orient: vertical;
   overflow: hidden;
`;

const Stats = styled.div`
   display: grid;
   grid-template-columns: repeat(3, 1fr);
   gap: 6px;
`;

const StatBox = styled.div<{ $highlight?: boolean }>`
   padding: 5px 8px;
   border-radius: 6px;
   text-align: center;
   background: ${({ $highlight }) =>
      $highlight ? `${colors.SUCCESS}10` : colors.GRAY14};
   border: 1px solid
      ${({ $highlight }) => ($highlight ? `${colors.SUCCESS}40` : colors.GRAY12)};
`;

const StatLabel = styled.p`
   font-size: 9px;
   color: ${colors.GRAY5};
   text-transform: uppercase;
   letter-spacing: 0.06em;
   font-weight: 700;
   margin: 0;
`;

const StatVal = styled.p<{ $green?: boolean }>`
   font-size: 13px;
   font-weight: 800;
   color: ${({ $green }) => ($green ? colors.SUCCESS : colors.GRAY_DARK)};
   margin: 1px 0 0;
`;

const BuyBtn = styled.button`
   width: 100%;
   margin-top: auto;
   padding: 9px 14px;
   border-radius: 8px;
   border: none;
   font-size: 11px;
   font-weight: 800;
   letter-spacing: 0.04em;
   text-transform: uppercase;
   color: ${colors.WHITE};
   background: linear-gradient(135deg, ${colors.PRIMARY} 0%, #6a4dff 55%, #8b5cf6 100%);
   box-shadow: 0 4px 12px rgba(90, 77, 255, 0.28);
   cursor: pointer;
   transition: opacity 0.15s ease;
   &:hover:not(:disabled) {
      opacity: 0.92;
   }
   &:disabled {
      background: ${colors.GRAY12};
      color: ${colors.GRAY5};
      cursor: not-allowed;
   }
`;
